// Enhanced SubmittedTickets.js with Home tab, filters + SLA dashboard + per-agent SLA analytics
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { db, storage } from "../firebase/config";
import {
  collection,
  getDocs,
  getDoc,
  doc,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { useAuth } from "../Context/AuthContext";
import { Link } from "react-router-dom";
import { getSlaPolicy, secondsUntil, ensureTicketSla } from "../utils/sla";
import {
  getHelpdeskSlaConfigured,
  setHelpdeskSlaConfigured,
} from "../utils/firstRunFlags";
import HelpdeskWelcomeTour from "../components/HelpdeskWelcomeTour";
import {
  getHelpdeskTourSeen,
  setHelpdeskTourSeen,
} from "../utils/helpdeskOnboardingState";

import Oiiko from "../tools/oiikotranslogo2.png";
import Oiiko2 from "../tools/oiikotranstxt.png";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { v4 as uuidv4 } from "uuid";

import { LuSettings } from "react-icons/lu";
import SLASettings from "./SLASettings";
import Slamodal from "../components/Slamodal";
import { toast } from "react-toastify";

import {
  FiInbox,
  FiCheckCircle,
  FiUserCheck,
  FiBarChart2,
  FiUser,
  FiPlus,
  FiHome, // 👈 NEW
} from "react-icons/fi";

import "../styles/SubmittedTickets.css";

function generateIncidentNo() {
  const date = new Date();
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `INC-${y}${m}${d}-${Math.floor(1000 + Math.random() * 9000)}`;
}

// ==========================
// NEW TICKET FORM (MODAL)
// ==========================
function NewTicketForm({
  companyId,
  submitterId,
  submitterName,
  onClose,
  onCreated,
}) {
  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "",
    impact: "",
    urgency: "",
    screenshotURL: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [screenshotFile, setScreenshotFile] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  function computePriority(impact, urgency) {
    const imp = (impact || "").toLowerCase();
    const urg = (urgency || "").toLowerCase();

    if (imp === "critical" || urg === "critical") return "Critical";
    if (imp === "high" && urg === "high") return "High";
    if (
      (imp === "high" && (urg === "medium" || urg === "low")) ||
      (urg === "high" && (imp === "medium" || imp === "low")) ||
      (imp === "medium" && urg === "medium")
    ) {
      return "Medium";
    }
    return "Low";
  }

  const uploadScreenshotToStorage = async (file) => {
    if (!file) return "";

    const safeName = file.name.replace(/\s+/g, "-");
    const fileRef = ref(
      storage,
      `tickets/${companyId}/screenshots/${uuidv4()}-${safeName}`,
    );

    await uploadBytes(fileRef, file);
    const url = await getDownloadURL(fileRef);
    return url;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!companyId) {
      setError("Missing company ID.");
      return;
    }

    if (!form.title.trim() || !form.description.trim()) {
      setError("Title and description are required.");
      return;
    }
    if (!form.category) {
      setError("Please select a category.");
      return;
    }
    if (!form.impact || !form.urgency) {
      setError("Please select both impact and urgency.");
      return;
    }

    setSaving(true);
    try {
      const priority = computePriority(form.impact, form.urgency);
      const incidentNo = generateIncidentNo();
      const now = new Date();

      let screenshotURL = form.screenshotURL;
      if (screenshotFile) {
        screenshotURL = await uploadScreenshotToStorage(screenshotFile);
      }

      const baseTicket = {
        incidentNo,
        title: form.title.trim(),
        description: form.description.trim(),
        category: form.category,
        impact: form.impact,
        urgency: form.urgency,
        priority,
        status: "Open",
        submittedBy: submitterId || null,
        submittedByName: submitterName || null,
        screenshotURL: screenshotURL || "",
        createdAt: now,
        updatedAt: now,
      };

      const ticketsRef = collection(db, `companies/${companyId}/tickets`);
      const docRef = await addDoc(ticketsRef, {
        ...baseTicket,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      try {
        const policy = await getSlaPolicy(companyId);
        await ensureTicketSla(companyId, docRef.id, baseTicket, policy);
      } catch (e) {
        console.warn("SLA init skipped in NewTicketForm:", e);
      }

      toast.success("Ticket created.");

      setForm({
        title: "",
        description: "",
        category: "",
        impact: "",
        urgency: "",
        screenshotURL: "",
      });
      setScreenshotFile(null);

      onCreated?.(true);
      onClose?.();
    } catch (err) {
      console.error("Failed to create ticket", err);
      setError("Failed to create ticket. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="newticket-form" onSubmit={handleSubmit}>
      {/* Top header row */}
      <div className="newticket-header">
        <div className="newticket-header-left">
          <h2 className="newticket-title">Create ticket</h2>
          <p className="newticket-sub">
            Give a clear summary and a few details. This helps IT route and
            resolve your issue faster.
          </p>
        </div>
      </div>

      {error && <div className="newticket-error-banner">{error}</div>}

      {/* Two-column layout */}
      <div className="newticket-grid">
        {/* LEFT: core details */}
        <div className="newticket-column">
          <div className="newticket-section">
            <div className="newticket-section-header">
              <h3>Issue details</h3>
              <span className="newticket-chip">Required</span>
            </div>

            <div className="newticket-row">
              <div className="newticket-field">
                <label className="newticket-label">
                  Title <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  placeholder="Short summary of the issue"
                />
                <p className="newticket-hint">
                  Example: “VPN not connecting on office Wi-Fi”
                </p>
              </div>

              <div className="newticket-field">
                <label className="newticket-label">
                  Category <span className="required">*</span>
                </label>
                <select
                  name="category"
                  value={form.category}
                  onChange={handleChange}
                >
                  <option value="">Select category…</option>
                  <option value="Inquiry/Help">Inquiry / How-to</option>
                  <option value="Hardware">Hardware</option>
                  <option value="Software">Software</option>
                  <option value="Network">Network</option>
                  <option value="Database">Database</option>
                </select>
              </div>
            </div>

            <div className="newticket-field">
              <label className="newticket-label">
                Description <span className="required">*</span>
              </label>
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                rows={5}
                placeholder="Describe what is happening, any error messages, and what you’ve tried so far."
              />
            </div>
          </div>
        </div>

        {/* RIGHT: impact / urgency / attachment */}
        <div className="newticket-column">
          <div className="newticket-section">
            <div className="newticket-section-header">
              <h3>Impact & urgency</h3>
              <span className="newticket-chip neutral">Helps set priority</span>
            </div>

            <div className="newticket-row stack-mobile">
              <div className="newticket-field">
                <label className="newticket-label">
                  Impact <span className="required">*</span>
                </label>
                <select
                  name="impact"
                  value={form.impact}
                  onChange={handleChange}
                >
                  <option value="">Select impact…</option>
                  <option value="Low">Low – affects only me</option>
                  <option value="Medium">Medium – affects small group</option>
                  <option value="High">
                    High – affects team or department
                  </option>
                  <option value="Critical">
                    Critical – system-wide or business-stopping
                  </option>
                </select>
              </div>

              <div className="newticket-field">
                <label className="newticket-label">
                  Urgency <span className="required">*</span>
                </label>
                <select
                  name="urgency"
                  value={form.urgency}
                  onChange={handleChange}
                >
                  <option value="">Select urgency…</option>
                  <option value="Low">Low – whenever possible</option>
                  <option value="Medium">Medium – today</option>
                  <option value="High">High – within a few hours</option>
                  <option value="Critical">Critical – immediately</option>
                </select>
              </div>
            </div>
          </div>

          <div className="newticket-section">
            <div className="newticket-section-header">
              <h3>Attachments</h3>
              <span className="newticket-chip optional">Optional</span>
            </div>

            <div className="newticket-field">
              <label className="newticket-label">
                Attach screenshot
                <span className="newticket-label-muted">
                  &nbsp;(images only)
                </span>
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  setScreenshotFile(file);
                }}
              />
              <p className="newticket-hint">
                A clear screenshot of the error or screen often speeds up
                resolution.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer actions */}
      <div className="newticket-footer">
        <button
          type="button"
          className="general-cancelbuttons"
          onClick={onClose}
          disabled={saving}
        >
          Cancel
        </button>
        <button type="submit" className="general-savebuttons" disabled={saving}>
          {saving ? "Creating…" : "Create ticket"}
        </button>
      </div>
    </form>
  );
}

export default function SubmittedTickets() {
  const { user } = useAuth();
  const [companyId, setCompanyId] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [employees, setEmployees] = useState({});
  const [loading, setLoading] = useState(true);
  const [slaFirstRunChecked, setSlaFirstRunChecked] = useState(false);
  const [showHelpdeskTour, setShowHelpdeskTour] = useState(false);
  const [helpdeskTourChecked, setHelpdeskTourChecked] = useState(false);
  const [error, setError] = useState("");
  // 👉 NEW default tab = "home"
  const [activeTab, setActiveTab] = useState("home");
  const [showNewTicket, setShowNewTicket] = useState(false);

  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const saved = localStorage.getItem("oiko_sidebar_open");
    return saved ? JSON.parse(saved) : false; // start closed by default
  });

  useEffect(() => {
    localStorage.setItem("oiko_sidebar_open", JSON.stringify(sidebarOpen));
  }, [sidebarOpen]);

  const [myGroup, setMyGroup] = useState(null);
  const [employeeName, setEmployeeName] = useState("");
  const [profileOpen, setProfileOpen] = useState(false);
  const profileBtnRef = useRef(null);
  const profilePopRef = useRef(null);

  const [myHelpdeskRole, setMyHelpdeskRole] = useState(null);
  const [slaPolicy, setSlaPolicy] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    incidentNo: "",
    impact: "",
    status: "",
    priority: "",
    submittedBy: "",
    claimedBy: "",
    category: "",
    slaStatus: "",
  });

  const [nameMap, setNameMap] = useState({});
  // Per-tab pagination
  const [pagination, setPagination] = useState({
    open: { page: 1, pageSize: 25 },
    mine: { page: 1, pageSize: 25 },
    claimed: { page: 1, pageSize: 25 },
    resolved: { page: 1, pageSize: 25 },
  });

  const updatePage = (type, nextPage) => {
    setPagination((prev) => {
      const cfg = prev[type] || { page: 1, pageSize: 25 };
      return {
        ...prev,
        [type]: { ...cfg, page: nextPage },
      };
    });
  };

  const updatePageSize = (type, newSize) => {
    setPagination((prev) => ({
      ...prev,
      [type]: { page: 1, pageSize: Number(newSize) || 25 },
    }));
  };

  // ---------------------------
  // NEW: SLA card preferences
  // ---------------------------
  const defaultCardPrefs = {
    metRate: true,
    frMet: true,
    resMet: true,
    paused: true,
    volumeTotal: true,
    volumeWindow: true,
    avgFr: true,
    avgRes: true,
  };

  const [cardPrefs, setCardPrefs] = useState(defaultCardPrefs);
  const [cardModalOpen, setCardModalOpen] = useState(false);

  // simple re-render tick for SLA chips (optional)
  useEffect(() => {
    const id = setInterval(() => {
      setTickets((t) => [...t]);
    }, 30_000); // every 30s
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!companyId) return;
    (async () => {
      try {
        setSlaPolicy(await getSlaPolicy(companyId));
      } catch {
        /* ignore */
      }
    })();
  }, [companyId]);

  // Load saved card preferences when companyId changes
  useEffect(() => {
    if (!companyId) return;
    const key = `oiko_sla_cards_${companyId}`;
    const raw = localStorage.getItem(key);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      setCardPrefs((prev) => ({ ...prev, ...parsed }));
    } catch (e) {
      console.warn("Failed to parse SLA card prefs:", e);
    }
  }, [companyId]);

  const handleSaveCardPrefs = useCallback(() => {
    if (!companyId) {
      setCardModalOpen(false);
      return;
    }
    const key = `oiko_sla_cards_${companyId}`;
    localStorage.setItem(key, JSON.stringify(cardPrefs));
    toast.success("Card preferences updated.");
    setCardModalOpen(false);
  }, [companyId, cardPrefs]);

  // build name map (employees + employers)
  useEffect(() => {
    if (!companyId) return;
    (async () => {
      try {
        const map = {};

        // employees
        const empSnap = await getDocs(
          collection(db, `companies/${companyId}/employees`),
        );
        empSnap.docs.forEach((d) => {
          const e = d.data();
          map[d.id] = e.name || e.username || e.email || d.id;
        });

        // employers (top-level users with this companyId)
        const usersSnap = await getDocs(collection(db, "users"));
        usersSnap.docs.forEach((d) => {
          const u = d.data();
          if (u.companyId === companyId) {
            map[d.id] = u.name || u.email || d.id;
          }
        });

        setNameMap(map);
      } catch {
        /* non-fatal */
      }
    })();
  }, [companyId]);

  // resolve companyId (employer or employee)
  useEffect(() => {
    if (!user) return;

    if (user.companyId) {
      setCompanyId(user.companyId);
      return;
    }

    (async () => {
      try {
        setLoading(true);
        const companiesSnapshot = await getDocs(collection(db, "companies"));
        for (const companyDoc of companiesSnapshot.docs) {
          const empDoc = await getDoc(
            doc(db, `companies/${companyDoc.id}/employees`, user.uid),
          );
          if (empDoc.exists()) {
            setCompanyId(companyDoc.id);
            break;
          }
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  // basic employee map (id -> name)
  useEffect(() => {
    if (!companyId) return;
    const fetchEmployees = async () => {
      try {
        const snapshot = await getDocs(
          collection(db, `companies/${companyId}/employees`),
        );
        const data = {};
        snapshot.docs.forEach((d) => {
          data[d.id] = d.data().name;
        });
        setEmployees(data);
      } catch {
        setError("Failed to fetch employees.");
      }
    };
    fetchEmployees();
  }, [companyId]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!profileOpen) return;
      const btn = profileBtnRef.current;
      const pop = profilePopRef.current;
      if (pop && !pop.contains(e.target) && btn && !btn.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    const onKey = (e) => e.key === "Escape" && setProfileOpen(false);
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [profileOpen]);

  const firstWord = (s = "") => s.trim().split(/\s+/)[0];

  // resolve display name in popover
  useEffect(() => {
    if (!user) return;

    const basicFallback =
      user.displayName ||
      user.name ||
      (user.email ? user.email.split("@")[0] : "there");

    if (nameMap[user.uid]) {
      setEmployeeName(firstWord(nameMap[user.uid]));
      return;
    }

    (async () => {
      try {
        if (companyId) {
          const empRef = doc(db, `companies/${companyId}/employees`, user.uid);
          const empSnap = await getDoc(empRef);
          if (empSnap.exists()) {
            const emp = empSnap.data();
            setEmployeeName(
              firstWord(emp.name || emp.username || emp.email || basicFallback),
            );
            return;
          }
        }

        const uRef = doc(db, "users", user.uid);
        const uSnap = await getDoc(uRef);
        if (uSnap.exists()) {
          const u = uSnap.data();
          setEmployeeName(
            firstWord(u.name || u.displayName || u.email || basicFallback),
          );
          return;
        }

        setEmployeeName(firstWord(basicFallback));
      } catch (e) {
        console.warn("Name resolve fallback due to error:", e);
        setEmployeeName(firstWord(basicFallback));
      }
    })();
  }, [user, companyId, nameMap]);

  const handleSlaSaved = useCallback(async () => {
    // Mark feature as configured so we never auto-open again
    if (companyId) {
      try {
        await setHelpdeskSlaConfigured(db, companyId);
      } catch (e) {
        console.warn("Failed to set helpdesk SLA configured flag:", e);
      }
    }

    // Re-pull policy so dashboard chips / warnings use the new values
    if (companyId) {
      try {
        const policy = await getSlaPolicy(companyId);
        setSlaPolicy(policy);
      } catch (e) {
        // optional ignore
      }
    }

    toast.success("SLA settings updated.");
  }, [companyId]);

  // load tickets
  const refreshTickets = useCallback(
    async (silent = false) => {
      if (!companyId) return;
      try {
        if (!silent) setLoading(true);
        const snapshot = await getDocs(
          collection(db, `companies/${companyId}/tickets`),
        );
        setTickets(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch {
        setError("Failed to fetch tickets.");
      } finally {
        setLoading(false);
      }
    },
    [companyId],
  );

  useEffect(() => {
    if (!companyId) return;
    refreshTickets();
  }, [companyId, refreshTickets]);

  // background refresh every 30s
  useEffect(() => {
    if (!companyId) return;
    const id = setInterval(() => {
      refreshTickets(true);
    }, 30_000);
    return () => clearInterval(id);
  }, [companyId, refreshTickets]);

  // refresh on focus / visibility
  useEffect(() => {
    const onFocus = () => refreshTickets(true);
    const onVisible = () => {
      if (document.visibilityState === "visible") refreshTickets(true);
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refreshTickets]);

  // refresh when switching tabs
  useEffect(() => {
    refreshTickets(true);
  }, [activeTab, refreshTickets]);

  // get my helpdesk group/role
  useEffect(() => {
    if (!companyId || !user) return;
    (async () => {
      try {
        const meSnap = await getDoc(
          doc(db, `companies/${companyId}/employees`, user.uid),
        );
        if (meSnap.exists()) {
          const me = meSnap.data();
          setMyGroup((me.group || "").toUpperCase());
          setMyHelpdeskRole((me.helpdeskRole || "").toLowerCase());
        }
      } catch (e) {
        console.error("Failed to fetch my employee doc", e);
      }
    })();
  }, [companyId, user]);

  // seconds → "Xm" / "Xh Ym"
  function fmtLeft(secs) {
    const s = Math.max(0, Math.floor(secs));
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    const rm = m % 60;
    return `${h}h ${rm}m`;
  }

  // Employers + admins
  const isEmployer =
    (user?.role && user.role !== "employee") || user?.isAdmin === true;

  // Help desk managers (IT group + manager role)
  const isHelpdeskManager = myGroup === "IT" && myHelpdeskRole === "manager";

  // Who can create tickets directly in the help desk
  const canCreateTicket = myGroup === "IT" || isEmployer;

  // Who can see SLA and full resolved view
  const canSeeSla = isEmployer || isHelpdeskManager;

  useEffect(() => {
    if (!companyId) return;
    if (!user?.uid) return;

    // avoid reruns
    if (helpdeskTourChecked) return;

    (async () => {
      try {
        const seen = await getHelpdeskTourSeen(db, companyId);
        if (!seen) setShowHelpdeskTour(true);
      } catch (e) {
        console.warn("Helpdesk tour seen check failed:", e);
      } finally {
        setHelpdeskTourChecked(true);
      }
    })();
  }, [companyId, user?.uid, helpdeskTourChecked]);

  useEffect(() => {
    if (!companyId) return;

    // ✅ employer-only: ONLY the employer gets auto-prompted on first run
    if (!isEmployer) return;

    // avoid reruns
    if (slaFirstRunChecked) return;

    (async () => {
      try {
        const configured = await getHelpdeskSlaConfigured(db, companyId);
        if (!configured) setSettingsOpen(true);
      } catch (e) {
        console.warn("Helpdesk SLA first-run check failed:", e);
      } finally {
        setSlaFirstRunChecked(true);
      }
    })();
  }, [companyId, isEmployer, slaFirstRunChecked]);

  const canSeeAllResolved = isEmployer || isHelpdeskManager;

  // filtered tickets (by header filters)
  const filteredTickets = tickets.filter((t) => {
    // SLA filtering
    if (filters.slaStatus === "breached") {
      const breached =
        t?.sla?.breachedFirstResponse || t?.sla?.breachedResolution;
      if (!breached) return false;
    }

    if (filters.slaStatus === "overdue") {
      const dueFr = t?.sla?.firstResponseDueAt;
      const dueRes = t?.sla?.resolutionDueAt;

      const frOverdue =
        dueFr && secondsUntil(dueFr) < 0 && !t.sla.firstResponseAt;
      const resOverdue =
        dueRes && secondsUntil(dueRes) < 0 && t.status !== "Resolved";

      if (!frOverdue && !resOverdue) return false;
    }
    return (
      (filters.incidentNo === "" ||
        (t.incidentNo || "")
          .toLowerCase()
          .includes(filters.incidentNo.toLowerCase())) &&
      (filters.impact === "" || t.impact === filters.impact) &&
      (filters.status === "" || t.status === filters.status) &&
      (filters.priority === "" || t.priority === filters.priority) &&
      (filters.submittedBy === "" ||
        employees[t.submittedBy] === filters.submittedBy) &&
      (filters.claimedBy === "" || t.claimedBy === filters.claimedBy) &&
      (filters.category === "" || t.category === filters.category)
    );
  });

  // Normalized display name for current user
  const currentUserDisplayName =
    user?.displayName ||
    user?.name ||
    (user?.email ? user.email.split("@")[0] : "");

  // Open tickets = anything not resolved
  const openTickets = filteredTickets.filter((t) => t.status !== "Resolved");

  // All resolved tickets (used by managers / helpdesk managers)
  const resolvedTicketsAll = filteredTickets.filter(
    (t) => t.status === "Resolved",
  );

  // Only *my* resolved tickets (used by normal agents)
  const resolvedTicketsMine = filteredTickets.filter(
    (t) =>
      t.status === "Resolved" &&
      (t.resolvedBy === currentUserDisplayName ||
        t.claimedBy === currentUserDisplayName),
  );

  // My claimed tickets (all)
  const myClaimedTickets = filteredTickets.filter(
    (t) => t.claimedBy === currentUserDisplayName,
  );

  // 👉 NEW: Tickets I'm currently working on (claimed by me + not resolved)
  const myActiveTickets = useMemo(
    () =>
      filteredTickets.filter(
        (t) =>
          t.claimedBy === currentUserDisplayName &&
          t.status &&
          t.status !== "Resolved",
      ),
    [filteredTickets, currentUserDisplayName],
  );

  // Tickets I personally submitted (employer / agent view)
  const mySubmittedTickets = filteredTickets.filter(
    (t) =>
      t.submittedBy === user?.uid ||
      t.submittedByName === currentUserDisplayName,
  );

  const getUniqueValues = (field) => [
    ...new Set(tickets.map((t) => t[field]).filter(Boolean)),
  ];

  const renderFilterDropdown = (field, options) => (
    <select
      className="table-filter"
      value={filters[field]}
      onChange={(e) => setFilters({ ...filters, [field]: e.target.value })}
    >
      <option value="">All</option>
      {options.map((val) => (
        <option key={val} value={val}>
          {val}
        </option>
      ))}
    </select>
  );

  function SlaChip({ due, breached, warnMins, labelWhenMet }) {
    const secsLeft = secondsUntil(due);
    const minsLeft = Math.ceil(secsLeft / 60);

    let cls = "sla-chip";
    if (breached || minsLeft < 0) cls += " breached";
    else if (minsLeft <= (warnMins ?? 30)) cls += " warn";

    const label = breached
      ? "Breached"
      : labelWhenMet
      ? labelWhenMet
      : minsLeft >= 0
      ? `${minsLeft}m left`
      : `${Math.abs(minsLeft)}m overdue`;

    return <span className={cls}>{label}</span>;
  }

  // ----- SLA Dashboard derivations -----
  const openNow = useMemo(
    () => tickets.filter((t) => t.status !== "Resolved"),
    [tickets],
  );

  function currentDueFor(t) {
    if (t?.sla?.paused) return null;
    if (!t?.sla) return null;

    const frDue = t.sla.firstResponseDueAt;
    const frMet = !!t.sla.firstResponseAt;
    const resDue = t.sla.resolutionDueAt;

    if (!frMet && frDue) return frDue;
    if (resDue) return resDue;

    return null;
  }

  const kpiOpenBreached = useMemo(
    () =>
      openNow.filter(
        (t) => t?.sla?.breachedFirstResponse || t?.sla?.breachedResolution,
      ).length,
    [openNow],
  );

  const kpiOpenAtRisk = useMemo(() => {
    return openNow.filter((t) => {
      if (t?.sla?.paused) return false;
      if (t?.sla?.breachedFirstResponse || t?.sla?.breachedResolution)
        return false;
      const due = currentDueFor(t);
      if (!due) return false;
      const secs = secondsUntil(due);
      const warn =
        t.sla?.policyTargets?.warnMinutesBeforeBreach ??
        slaPolicy?.warnMinutesBeforeBreach ??
        30;
      return secs > 0 && secs <= warn * 60;
    }).length;
  }, [openNow, slaPolicy]);

  const kpiPaused = useMemo(
    () => openNow.filter((t) => t?.sla?.paused).length,
    [openNow],
  );
  const overdueFirstResponseOpen = useMemo(
    () =>
      openNow.filter((t) => {
        if (!t?.sla || t.sla.paused) return false;
        const dueFr = t.sla.firstResponseDueAt;
        if (!dueFr) return false;

        const secs = secondsUntil(dueFr);
        const frDone = !!t.sla.firstResponseAt;

        // overdue = past due + no first response yet
        return secs < 0 && !frDone;
      }).length,
    [openNow],
  );

  const overdueResolutionOpen = useMemo(
    () =>
      openNow.filter((t) => {
        if (!t?.sla || t.sla.paused) return false;
        const dueRes = t.sla.resolutionDueAt;
        if (!dueRes) return false;

        const secs = secondsUntil(dueRes);

        // overdue = past due + ticket not resolved
        return secs < 0 && t.status !== "Resolved";
      }).length,
    [openNow],
  );

  const { metRatePct, metCount, totalResolved30d } = useMemo(() => {
    const THIRTY_D_MS = 30 * 24 * 3600 * 1000;
    const cutoff = Date.now() - THIRTY_D_MS;

    const resolved30 = tickets.filter((t) => {
      if (t.status !== "Resolved") return false;
      const created = t.createdAt?.toDate
        ? t.createdAt.toDate()
        : new Date(t.createdAt);
      if (!created || isNaN(created.getTime())) return false;
      return created.getTime() >= cutoff;
    });

    const met = resolved30.filter(
      (t) => t?.sla && t.sla.breachedResolution === false,
    ).length;
    const pct = resolved30.length
      ? Math.round((met / resolved30.length) * 100)
      : 0;

    return {
      metRatePct: pct,
      metCount: met,
      totalResolved30d: resolved30.length,
    };
  }, [tickets]);

  // SLA window: 7 / 30 / 90 days
  const [windowDays, setWindowDays] = useState(30);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const windowMs = windowDays * 24 * 3600 * 1000;
  const cutoffMs = Date.now() - windowMs;

  // Timestamp helpers
  const getMs = useCallback((x) => {
    if (!x) return NaN;
    if (typeof x?.toDate === "function") return x.toDate().getTime();
    if (x instanceof Date) return x.getTime();
    const d = new Date(x);
    return Number.isNaN(d.getTime()) ? NaN : d.getTime();
  }, []);

  const createdMs = useCallback((t) => getMs(t.createdAt), [getMs]);

  const fmtDur = (ms) => {
    const m = Math.round(ms / 60000);
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    const rm = m % 60;
    return `${h}h ${rm}m`;
  };

  const lastWindow = useMemo(
    () =>
      tickets.filter((t) => {
        const ms = createdMs(t);
        return Number.isFinite(ms) && ms >= cutoffMs;
      }),
    [tickets, cutoffMs, createdMs],
  );

  const totals = useMemo(
    () => ({
      totalTickets: tickets.length,
      openCount: tickets.filter((t) => t.status !== "Resolved").length,
      resolvedCount: tickets.filter((t) => t.status === "Resolved").length,
      submittedInWindow: lastWindow.length,
    }),
    [tickets, lastWindow],
  );

  const frStats = useMemo(() => {
    const met = lastWindow.filter((t) => t?.sla?.firstResponseAt).length;
    const breached = lastWindow.filter((t) => {
      const b = t?.sla?.breachedFirstResponse;
      if (b === true) return true;
      const frAt = t?.sla?.firstResponseAt;
      const frDue = t?.sla?.firstResponseDueAt;
      if (!frAt || !frDue) return false;
      return getMs(frAt) > getMs(frDue);
    }).length;

    const durationsMs = lastWindow
      .filter((t) => t?.sla?.firstResponseAt)
      .map((t) => Math.max(0, getMs(t.sla.firstResponseAt) - createdMs(t)));

    const avgMs =
      durationsMs.length === 0
        ? 0
        : Math.round(
            durationsMs.reduce((a, b) => a + b, 0) / durationsMs.length,
          );

    return { met, breached, avgMs };
  }, [lastWindow, createdMs, getMs]);

  const resStats = useMemo(() => {
    const resolved = lastWindow.filter((t) => t.status === "Resolved");

    const met = resolved.filter(
      (t) => t?.sla && t.sla.breachedResolution === false,
    ).length;
    const breached = resolved.filter(
      (t) => t?.sla && t.sla.breachedResolution === true,
    ).length;

    const durationsMs = resolved
      .filter((t) => t?.resolvedAt)
      .map((t) => Math.max(0, getMs(t.resolvedAt) - createdMs(t)));

    const avgMs =
      durationsMs.length === 0
        ? 0
        : Math.round(
            durationsMs.reduce((a, b) => a + b, 0) / durationsMs.length,
          );

    return { met, breached, avgMs, totalResolved: resolved.length };
  }, [lastWindow, createdMs, getMs]);

  // --- NEW: per-agent SLA performance within window ---
  const agentStats = useMemo(() => {
    const map = {};

    lastWindow.forEach((t) => {
      if (!t.claimedBy) return; // only tickets actually worked by someone

      const agentName = t.claimedBy;
      if (!map[agentName]) {
        map[agentName] = {
          name: agentName,
          total: 0,

          frTotal: 0,
          frMet: 0,
          frBreached: 0,
          frDurations: [],

          resTotal: 0,
          resMet: 0,
          resBreached: 0,
          resDurations: [],
        };
      }

      const a = map[agentName];
      a.total += 1;

      // First response
      if (t.sla?.firstResponseDueAt) {
        a.frTotal += 1;

        if (t.sla?.breachedFirstResponse === false) {
          a.frMet += 1;
        } else if (t.sla?.breachedFirstResponse === true) {
          a.frBreached += 1;
        }

        if (t.sla?.firstResponseAt) {
          const d = Math.max(0, getMs(t.sla.firstResponseAt) - createdMs(t));
          if (Number.isFinite(d)) a.frDurations.push(d);
        }
      }

      // Resolution
      if (t.sla?.resolutionDueAt && t.status === "Resolved") {
        a.resTotal += 1;

        if (t.sla?.breachedResolution === false) {
          a.resMet += 1;
        } else if (t.sla?.breachedResolution === true) {
          a.resBreached += 1;
        }

        if (t.resolvedAt) {
          const d = Math.max(0, getMs(t.resolvedAt) - createdMs(t));
          if (Number.isFinite(d)) a.resDurations.push(d);
        }
      }
    });

    const arr = Object.values(map).map((a) => {
      const avg = (xs) =>
        xs.length ? Math.round(xs.reduce((x, y) => x + y, 0) / xs.length) : 0;

      const frMetPct =
        a.frTotal === 0 ? 0 : Math.round((a.frMet / a.frTotal) * 100);
      const resMetPct =
        a.resTotal === 0 ? 0 : Math.round((a.resMet / a.resTotal) * 100);

      return {
        ...a,
        frMetPct,
        resMetPct,
        avgFrMs: avg(a.frDurations),
        avgResMs: avg(a.resDurations),
      };
    });

    return arr;
  }, [lastWindow, createdMs, getMs]);

  // when agentStats loads, auto-select the first agent
  useEffect(() => {
    if (!selectedAgent && agentStats.length > 0) {
      setSelectedAgent(agentStats[0].name);
    }
  }, [agentStats, selectedAgent]);
  // selected agent stats + tickets inside the current window
  const selectedAgentStats = useMemo(
    () => agentStats.find((a) => a.name === selectedAgent) || null,
    [agentStats, selectedAgent],
  );

  const selectedAgentTickets = useMemo(
    () =>
      selectedAgent
        ? lastWindow.filter((t) => t.claimedBy === selectedAgent)
        : [],
    [lastWindow, selectedAgent],
  );

  // 👉 NEW: "resolved this week" for current user (Mon–Sun)
  const resolvedThisWeekCount = useMemo(() => {
    if (!tickets.length) return 0;

    const now = new Date();
    const day = now.getDay(); // 0 = Sun, 1 = Mon, ...
    const diffToMonday = (day === 0 ? -6 : 1) - day; // shift to Monday
    const weekStart = new Date(now);
    weekStart.setHours(0, 0, 0, 0);
    weekStart.setDate(weekStart.getDate() + diffToMonday);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 7);

    return tickets.filter((t) => {
      if (t.status !== "Resolved") return false;
      if (
        !(
          t.resolvedBy === currentUserDisplayName ||
          t.claimedBy === currentUserDisplayName
        )
      ) {
        return false;
      }

      const ms = getMs(t.resolvedAt || t.updatedAt || t.createdAt);
      if (!Number.isFinite(ms)) return false;
      const d = new Date(ms);
      return d >= weekStart && d < weekEnd;
    }).length;
  }, [tickets, currentUserDisplayName, getMs]);

  const renderTable = (rows, type) => {
    if (rows.length === 0) {
      return <p>No {type} tickets available.</p>;
    }

    // Pull pagination config for this table
    const cfg = pagination[type] || { page: 1, pageSize: 25 };
    const pageSize = cfg.pageSize || 25;
    const total = rows.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    // Clamp page so filters don't leave us on an empty page
    const currentPage = Math.min(cfg.page, totalPages);
    const startIndex = (currentPage - 1) * pageSize;
    const pageRows = rows.slice(startIndex, startIndex + pageSize);

    const from = total === 0 ? 0 : startIndex + 1;
    const to = startIndex + pageRows.length;

    return (
      <div className="submittedtickets-table-scroll">
        <div className="submittedtickets-table-wrapper">
          <table className="itsubmittedticket-table">
            <thead>
              <tr>
                <th>Date Created</th>
                <th>
                  Incident No
                  <input
                    className="table-search"
                    placeholder="Search..."
                    value={filters.incidentNo}
                    onChange={(e) =>
                      setFilters({ ...filters, incidentNo: e.target.value })
                    }
                  />
                </th>
                <th>Title</th>
                <th>
                  Impact{" "}
                  {renderFilterDropdown("impact", getUniqueValues("impact"))}
                </th>
                <th>Urgency</th>
                <th>
                  Status{" "}
                  {renderFilterDropdown("status", getUniqueValues("status"))}
                </th>
                <th>
                  Category{" "}
                  {renderFilterDropdown(
                    "category",
                    getUniqueValues("category"),
                  )}
                </th>

                <th>
                  Priority{" "}
                  {renderFilterDropdown(
                    "priority",
                    getUniqueValues("priority"),
                  )}
                </th>

                <th>
                  Submitted By{" "}
                  {renderFilterDropdown(
                    "submittedBy",
                    Object.values(employees),
                  )}
                </th>
                {type !== "claimed" && (
                  <th>
                    Claimed By{" "}
                    {renderFilterDropdown(
                      "claimedBy",
                      getUniqueValues("claimedBy"),
                    )}
                  </th>
                )}
                {type === "resolved" && <th>Resolved By</th>}
                {type === "resolved" && <th>Resolved At</th>}
                <th>Screenshot</th>
                <th>Action</th>

                <th>First Response SLA</th>
                <th>Resolution SLA</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((ticket) => (
                <tr key={ticket.id}>
                  <td>
                    {ticket.createdAt?.toDate
                      ? ticket.createdAt.toDate().toLocaleString()
                      : new Date(ticket.createdAt).toLocaleString()}
                  </td>
                  <td>{ticket.incidentNo}</td>
                  <td>{ticket.title}</td>
                  <td>{ticket.impact}</td>
                  <td>{ticket.urgency}</td>
                  {/* Status */}
                  <td>{ticket.status}</td>
                  <td>{ticket.category || "—"}</td>
                  {/* Priority with badge */}
                  <td>
                    <span
                      className={
                        "priority-pill " +
                        (ticket.priority ? ticket.priority.toLowerCase() : "")
                      }
                    >
                      {ticket.priority || "—"}
                    </span>
                  </td>

                  {/* Submitted By */}
                  <td>
                    {ticket.submittedByName ||
                      nameMap[ticket.submittedBy] ||
                      "Unknown"}
                  </td>

                  {/* Claimed / Resolved columns */}
                  {type !== "claimed" && (
                    <td>{ticket.claimedBy || "Not Claimed"}</td>
                  )}
                  {type === "resolved" && <td>{ticket.resolvedBy || "N/A"}</td>}
                  {type === "resolved" && (
                    <td>
                      {ticket.resolvedAt
                        ? ticket.resolvedAt.toDate
                          ? ticket.resolvedAt.toDate().toLocaleString()
                          : new Date(ticket.resolvedAt).toLocaleString()
                        : "N/A"}
                    </td>
                  )}

                  <td>
                    {ticket.screenshotURL ? (
                      <a
                        href={ticket.screenshotURL}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View Screenshot
                      </a>
                    ) : (
                      "No Screenshot"
                    )}
                  </td>
                  <td>
                    <Link to={`/ticket-details/${ticket.id}`}>
                      View Details
                    </Link>
                  </td>

                  {/* First Response SLA */}
                  <td>
                    {ticket.sla?.firstResponseDueAt ? (
                      <SlaChip
                        due={ticket.sla.firstResponseDueAt}
                        breached={ticket.sla?.breachedFirstResponse}
                        warnMins={
                          ticket.sla?.policyTargets?.warnMinutesBeforeBreach ??
                          slaPolicy?.warnMinutesBeforeBreach ??
                          30
                        }
                        labelWhenMet={
                          ticket.sla?.firstResponseAt ? "Met" : null
                        }
                      />
                    ) : (
                      "—"
                    )}
                  </td>

                  {/* Resolution SLA */}
                  <td>
                    {ticket.sla?.resolutionDueAt ? (
                      <SlaChip
                        due={ticket.sla.resolutionDueAt}
                        breached={ticket.sla?.breachedResolution}
                        warnMins={
                          ticket.sla?.policyTargets?.warnMinutesBeforeBreach ??
                          slaPolicy?.warnMinutesBeforeBreach ??
                          30
                        }
                        labelWhenMet={
                          ticket.status === "Resolved" ? "Met" : null
                        }
                      />
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination controls */}
        <div className="table-pagination-row">
          <div className="table-pagination-info">
            Showing {from}–{to} of {total}
          </div>
          <div className="table-pagination-controls">
            <label>
              Rows per page{" "}
              <select
                value={pageSize}
                onChange={(e) => updatePageSize(type, e.target.value)}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </label>
            <button
              type="button"
              onClick={() => updatePage(type, currentPage - 1)}
              disabled={currentPage <= 1}
            >
              Prev
            </button>
            <span className="table-pagination-page">
              {currentPage} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => updatePage(type, currentPage + 1)}
              disabled={currentPage >= totalPages}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    );
  };

  function KpiCard({
    icon: Icon,
    value,
    label,
    sublabel,
    tone = "neutral",
    children,
  }) {
    return (
      <div className={`sla-kpi-card tone-${tone}`}>
        <div className="sla-kpi-top">
          {Icon && <Icon className="sla-kpi-icon" />}
          <div className="sla-kpi-value">{value}</div>
        </div>
        <div className="sla-kpi-label">{label}</div>
        {sublabel && <div className="sla-kpi-sub">{sublabel}</div>}
        {children}
      </div>
    );
  }

  function PercentRing({ percent = 0, size = 64, stroke = 8 }) {
    const angle = Math.max(0, Math.min(100, percent)) * 3.6;
    const ringStyle = {
      width: size,
      height: size,
      background: `conic-gradient(var(--ring-ok) ${angle}deg, var(--ring-track) ${angle}deg 360deg)`,
      borderRadius: "50%",
      display: "grid",
      placeItems: "center",
    };
    const innerSize = size - stroke * 2;
    return (
      <div className="ring-wrap" style={ringStyle}>
        <div
          className="ring-inner"
          style={{ width: innerSize, height: innerSize }}
        >
          <span className="ring-text">{percent}%</span>
        </div>
      </div>
    );
  }

  // NEW: single row in the "Customize cards" modal
  function CardToggleRow({ id, title, description }) {
    const checked = cardPrefs[id];

    return (
      <div className="sla-card-toggle-row">
        <div className="sla-card-toggle-text">
          <div className="sla-card-toggle-title">{title}</div>
          <div className="sla-card-toggle-desc">{description}</div>
        </div>

        <label className="sla-switch">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) =>
              setCardPrefs((prev) => ({ ...prev, [id]: e.target.checked }))
            }
          />
          <span className="sla-switch-track">
            <span className="sla-switch-thumb" />
          </span>
        </label>
      </div>
    );
  }

  return (
    <div className="submittedtickets-layout-fixed">
      <nav className="helpdesk-navbar">
        <div className="helpdesknav-left">
          <button
            className="sidebar-toggle-btn"
            aria-label={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
            title={sidebarOpen ? "Collapse menu" : "Expand menu"}
            onClick={() => setSidebarOpen((v) => !v)}
          >
            {sidebarOpen ? "←" : "☰"}
          </button>
          <img src={Oiiko} alt="Oiiko" className="brand-logo" />
          <img src={Oiiko2} alt="Oiiko" className="brand-logo1" />
          <div className="helpdesknavbar-title"></div>
        </div>
        <div className="helpdesknav-right">
          {canCreateTicket && (
            <button
              className="helpdesknav-icon-button"
              title="Create Ticket"
              aria-label="Create Ticket"
              onClick={() => setShowNewTicket(true)}
            >
              <FiPlus className="helpdesknav-icon" />
            </button>
          )}

          <button
            ref={profileBtnRef}
            className="helpdesknav-icon-button"
            title="Profile"
            aria-haspopup="dialog"
            aria-expanded={profileOpen}
            aria-controls="profile-popover"
            onClick={() => setProfileOpen((v) => !v)}
          >
            <FiUser className="helpdesknav-icon profile-icon" />
          </button>

          {canSeeSla && (
            <button
              className="helpdesknav-icon-button"
              title="Settings"
              onClick={() => setSettingsOpen(true)}
            >
              <LuSettings className="helpdesknav-icon" />
            </button>
          )}

          {profileOpen && (
            <div
              id="profile-popover"
              ref={profilePopRef}
              role="dialog"
              aria-label="User menu"
              className="profile-popover"
            >
              <div className="profile-popover-header">
                Welcome back, <strong>{employeeName || "there"}</strong>
              </div>
              <div className="profile-popover-sub">{user?.email}</div>
            </div>
          )}
        </div>
      </nav>

      <div
        className={`submittedtickets-wrapper ${sidebarOpen ? "" : "collapsed"}`}
      >
        <div
          className={`submittedtickets-sidebar-fixed ${
            sidebarOpen ? "" : "collapsed"
          }`}
        >
          <ul className="hd-side">
            {[
              // Home
              { key: "home", label: "Home", Icon: FiHome },

              // 1. Open tickets
              { key: "open", label: "Open Tickets", Icon: FiInbox },

              // 2. My submitted tickets (history)
              { key: "mine", label: "My Submitted Tickets", Icon: FiUser },

              // 3. My claimed tickets
              {
                key: "claimed",
                label: "My Claimed Tickets",
                Icon: FiUserCheck,
              },

              // 4. Resolved tickets
              {
                key: "resolved",
                label: "Resolved Tickets",
                Icon: FiCheckCircle,
              },

              // 5. SLA dashboard (only for employers / helpdesk managers)
              ...(canSeeSla
                ? [{ key: "sla", label: "SLA Dashboard", Icon: FiBarChart2 }]
                : []),
            ].map(({ key, label, Icon }) => (
              <li key={key}>
                <button
                  className={`hd-side-item ${
                    activeTab === key ? "active" : ""
                  }`}
                  onClick={() => setActiveTab(key)}
                  title={label}
                  aria-label={label}
                >
                  <Icon className="sidebar-icon" />
                  <span className="sidebar-label">{label}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="submittedtickets-tab-content-fixed">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
            }}
          >
            {canSeeSla && (
              <button
                className="btn-sla-settings"
                onClick={() => setSettingsOpen(true)}
                style={{
                  background: "#256d34",
                  display: "none",
                  color: "#fff",
                  padding: "8px 12px",
                  borderRadius: 8,
                  border: "none",
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  whiteSpace: "nowrap",
                  cursor: "pointer",
                }}
              >
                SLA Settings
              </button>
            )}
          </div>

          {error && <p className="submittedtickets-error">{error}</p>}
          {loading && <p className="submittedtickets-loading">Loading...</p>}

          {/* NEW HOME TAB */}
          {activeTab === "home" && (
            <div className="sla-dashboard-page home-dashboard">
              {/* TOP CARDS */}
              <section className="sla-section">
                <div className="sla-kpi-grid">
                  <KpiCard
                    value={myActiveTickets.length}
                    label="In Progress"
                    tone="accent"
                  />
                  <KpiCard
                    value={resolvedThisWeekCount}
                    label="Resolved"
                    sublabel="This week"
                    tone="success"
                  />
                  <KpiCard
                    value={openTickets.length}
                    label="All Open tickets"
                    tone="info"
                  />
                </div>
              </section>

              {/* CURRENTLY WORKING ON LIST */}
              <section className="sla-section">
                <div className="sla-panel">
                  <div className="sla-panel-title-row">
                    <h3>Your Work</h3>
                    <span className="sla-panel-caption">
                      Tickets you&apos;ve claimed and are still in progress.
                    </span>
                  </div>

                  {myActiveTickets.length === 0 ? (
                    <p className="sla-empty-text">
                      You don&apos;t have any active tickets right now.
                    </p>
                  ) : (
                    <div className="sla-agent-tickets-list">
                      {myActiveTickets.map((t) => (
                        <div key={t.id} className="sla-agent-ticket-row">
                          <div className="sla-agent-ticket-main">
                            <strong>{t.incidentNo || t.id}</strong>
                            <span className="sla-ticket-title">
                              {t.title || "(no title)"}
                            </span>
                            <span
                              className={`priority-pill small ${t.priority?.toLowerCase() ||
                                ""}`}
                            >
                              {t.priority || "—"}
                            </span>
                          </div>
                          <div className="sla-agent-ticket-meta">
                            <span className="status-text">
                              {t.status || "In progress"}
                            </span>
                            <Link
                              to={`/ticket-details/${t.id}`}
                              className="sla-list-link"
                            >
                              View
                            </Link>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </section>
            </div>
          )}

          {activeTab === "open" && renderTable(openTickets, "open")}
          {activeTab === "mine" && renderTable(mySubmittedTickets, "mine")}
          {activeTab === "claimed" && renderTable(myClaimedTickets, "claimed")}
          {activeTab === "resolved" &&
            renderTable(
              canSeeAllResolved ? resolvedTicketsAll : resolvedTicketsMine,
              "resolved",
            )}

          {activeTab === "sla" && canSeeSla && (
            <div className="sla-dashboard-page">
              {/* HEADER: title + window selector + customize button */}
              <div className="sla-header-row">
                <div>
                  <h2 className="sla-page-title">Team SLA Performance</h2>
                  <p className="sla-page-subtitle">
                    Track how your help desk is meeting response & resolution
                    targets.
                  </p>
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    alignItems: "center",
                  }}
                >
                  <div className="sla-window-selector">
                    <label>Window</label>
                    <select
                      value={windowDays}
                      onChange={(e) => setWindowDays(Number(e.target.value))}
                    >
                      <option value={7}>Last 7 days</option>
                      <option value={30}>Last 30 days</option>
                      <option value={90}>Last 90 days</option>
                    </select>
                  </div>
                  <button
                    type="button"
                    className="sla-customize-btn"
                    onClick={() => setCardModalOpen(true)}
                  >
                    Customize cards
                  </button>
                </div>
              </div>

              {/* SECTION 1: TEAM OVERVIEW KPIs */}
              <section className="sla-section">
                <div className="home-dashboard">
                  {/* ROW 1 - BREACH / AT RISK (always visible) */}
                  <div className="sla-kpi-grid">
                    <KpiCard
                      value={kpiOpenBreached}
                      label="Open breached"
                      tone="danger"
                    />

                    <KpiCard
                      value={overdueFirstResponseOpen}
                      label="FR SLA overdue (open)"
                      tone="danger"
                    />

                    <KpiCard
                      value={overdueResolutionOpen}
                      label="Resolution SLA overdue (open)"
                      tone="danger"
                    />

                    <KpiCard
                      value={kpiOpenAtRisk}
                      label="Open at risk"
                      tone="warn"
                    />
                  </div>

                  {/* ROW 2 — SLA PERFORMANCE (customizable) */}
                  <div className="sla-kpi-grid">
                    {cardPrefs.metRate && (
                      <KpiCard
                        label={`Met rate (${windowDays}d)`}
                        sublabel={`${metCount}/${totalResolved30d} resolved`}
                        tone="success"
                      >
                        <div className="kpi-ring-row">
                          <PercentRing percent={metRatePct} />
                        </div>
                      </KpiCard>
                    )}

                    {cardPrefs.frMet && (
                      <KpiCard
                        value={frStats.met}
                        label={`First response met (${windowDays}d)`}
                        sublabel={`${frStats.breached} breached`}
                        tone="success"
                      />
                    )}

                    {cardPrefs.resMet && (
                      <KpiCard
                        value={resStats.met}
                        label={`Resolution met (${windowDays}d)`}
                        sublabel={`${resStats.breached} breached`}
                        tone="success"
                      />
                    )}

                    {cardPrefs.paused && (
                      <KpiCard value={kpiPaused} label="Paused" tone="info" />
                    )}
                  </div>

                  {/* ROW 3 — VOLUME & SPEED (customizable) */}
                  <div className="sla-kpi-grid extra">
                    {cardPrefs.volumeTotal && (
                      <KpiCard
                        value={totals.totalTickets}
                        label="All tickets (all-time)"
                        sublabel={`${totals.openCount} open • ${totals.resolvedCount} resolved`}
                        tone="info"
                      />
                    )}

                    {cardPrefs.volumeWindow && (
                      <KpiCard
                        value={totals.submittedInWindow}
                        label={`Submitted (${windowDays}d)`}
                        tone="accent"
                      />
                    )}

                    {cardPrefs.avgFr && (
                      <KpiCard
                        value={fmtDur(frStats.avgMs)}
                        label={`Avg first response (${windowDays}d)`}
                        tone="muted"
                      />
                    )}

                    {cardPrefs.avgRes && (
                      <KpiCard
                        value={fmtDur(resStats.avgMs)}
                        label={`Avg resolution (${windowDays}d)`}
                        tone="muted"
                      />
                    )}
                  </div>
                </div>
              </section>

              {/* SECTION 2: AGENT PERFORMANCE + DRILLDOWN */}
              <section className="sla-section">
                <div className="sla-two-column">
                  {/* LEFT: agents table */}
                  <div className="sla-agent-table-card">
                    <div className="sla-panel-title-row">
                      <h3>Agent performance</h3>
                      <span className="sla-panel-caption">
                        Click an agent to view their tickets
                      </span>
                    </div>

                    {agentStats.length === 0 ? (
                      <p className="sla-empty-text">
                        No agent activity in this window yet.
                      </p>
                    ) : (
                      <div className="submittedtickets-table-scroll">
                        <div className="submittedtickets-table-wrapper">
                          <table className="itsubmittedticket-table sla-agent-table">
                            <thead>
                              <tr>
                                <th>Agent</th>
                                <th>Tickets</th>
                                <th>FR Met %</th>
                                <th>Res Met %</th>
                                <th>Avg FR Time</th>
                                <th>Avg Res Time</th>
                                <th>FR Breached</th>
                                <th>Res Breached</th>
                              </tr>
                            </thead>
                            <tbody>
                              {agentStats.map((a) => {
                                const isTop = a.resMetPct >= 95 && a.total >= 5; // highlight stars
                                return (
                                  <tr
                                    key={a.name}
                                    className={
                                      "sla-agent-row" +
                                      (a.name === selectedAgent
                                        ? " selected"
                                        : "") +
                                      (isTop ? " top-performer" : "")
                                    }
                                    onClick={() => setSelectedAgent(a.name)}
                                  >
                                    <td>{a.name}</td>
                                    <td>{a.total}</td>
                                    <td>{a.frMetPct}%</td>
                                    <td>{a.resMetPct}%</td>
                                    <td>{fmtDur(a.avgFrMs)}</td>
                                    <td>{fmtDur(a.avgResMs)}</td>
                                    <td>{a.frBreached}</td>
                                    <td>{a.resBreached}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* RIGHT: selected agent detail */}
                  <div className="sla-agent-detail-card">
                    {selectedAgentStats ? (
                      <>
                        <div className="sla-panel-title-row">
                          <h3>{selectedAgentStats.name}</h3>
                          <span className="sla-pill">
                            {selectedAgentStats.total} tickets in {windowDays}d
                          </span>
                        </div>

                        <div className="sla-agent-kpi-row">
                          <div className="sla-agent-kpi">
                            <span className="label">FR met</span>
                            <span className="value">
                              {selectedAgentStats.frMetPct}%
                            </span>
                          </div>
                          <div className="sla-agent-kpi">
                            <span className="label">Resolution met</span>
                            <span className="value">
                              {selectedAgentStats.resMetPct}%
                            </span>
                          </div>
                          <div className="sla-agent-kpi">
                            <span className="label">Avg FR time</span>
                            <span className="value">
                              {fmtDur(selectedAgentStats.avgFrMs)}
                            </span>
                          </div>
                          <div className="sla-agent-kpi">
                            <span className="label">Avg res time</span>
                            <span className="value">
                              {fmtDur(selectedAgentStats.avgResMs)}
                            </span>
                          </div>
                        </div>

                        <div className="sla-agent-tickets-header">
                          <h4>Tickets handled</h4>
                          <span className="sla-panel-caption">
                            Showing tickets claimed by this agent within the
                            window.
                          </span>
                        </div>

                        {selectedAgentTickets.length === 0 ? (
                          <p className="sla-empty-text">
                            No tickets for this agent in the selected window.
                          </p>
                        ) : (
                          <div className="sla-agent-tickets-list">
                            {selectedAgentTickets.map((t) => {
                              let slaLabel = null;
                              let slaClass = "sla-badge";
                              let showBadge = false;

                              // Only care if there is a resolution SLA at all
                              if (t.sla?.resolutionDueAt) {
                                showBadge = true;

                                // 1) Resolved tickets: show static Met/Breached, no timer
                                if (t.status === "Resolved") {
                                  if (t.sla?.breachedResolution) {
                                    slaClass += " breached";
                                    slaLabel = "Breached";
                                  } else {
                                    slaClass += " ok";
                                    slaLabel = "Met";
                                  }
                                }
                                // 2) Paused timers
                                else if (t.sla?.paused) {
                                  slaLabel = "Paused";
                                }
                                // 3) Still active: show countdown
                                else {
                                  const due = currentDueFor(t);
                                  if (due) {
                                    const secs = secondsUntil(due);
                                    const warnSeconds =
                                      (t.sla?.policyTargets
                                        ?.warnMinutesBeforeBreach ??
                                        slaPolicy?.warnMinutesBeforeBreach ??
                                        30) * 60;

                                    const breached =
                                      secs < 0 ||
                                      t?.sla?.breachedFirstResponse ||
                                      t?.sla?.breachedResolution;

                                    const left = fmtLeft(Math.abs(secs));

                                    if (breached) {
                                      slaClass += " breached";
                                      slaLabel = `Overdue ${left}`;
                                    } else if (secs <= warnSeconds) {
                                      slaClass += " warn";
                                      slaLabel = `${left} left`;
                                    } else {
                                      slaClass += " ok";
                                      slaLabel = `${left} left`;
                                    }
                                  } else {
                                    // has SLA object but no current due (fallback)
                                    slaLabel = "—";
                                  }
                                }
                              }

                              return (
                                <div
                                  key={t.id}
                                  className="sla-agent-ticket-row"
                                >
                                  <div className="sla-agent-ticket-main">
                                    <strong>{t.incidentNo || t.id}</strong>
                                    <span className="sla-ticket-title">
                                      {t.title || "(no title)"}
                                    </span>
                                    <span
                                      className={`priority-pill small ${t.priority?.toLowerCase() ||
                                        ""}`}
                                    >
                                      {t.priority || "—"}
                                    </span>
                                  </div>
                                  <div className="sla-agent-ticket-meta">
                                    <span className="status-text">
                                      {t.status}
                                    </span>
                                    {showBadge && (
                                      <span className={slaClass}>
                                        {slaLabel}
                                      </span>
                                    )}

                                    <Link
                                      to={`/ticket-details/${t.id}`}
                                      className="sla-list-link"
                                    >
                                      View
                                    </Link>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="sla-empty-text">
                        Select an agent on the left to see their performance.
                      </p>
                    )}
                  </div>
                </div>
              </section>
            </div>
          )}

          {/* SLA Settings modal */}
          <Slamodal
            isOpen={settingsOpen}
            onClose={() => setSettingsOpen(false)}
            title="SLA Settings"
          >
            <SLASettings
              onSaved={handleSlaSaved}
              onClose={() => setSettingsOpen(false)}
            />
          </Slamodal>

          {/* NEW: Customize cards modal */}
          <Slamodal
            isOpen={cardModalOpen}
            onClose={() => setCardModalOpen(false)}
            title="Customize summary cards"
          >
            <div className="sla-card-toggle-modal">
              <p className="sla-card-toggle-intro">
                Choose which summary cards to show in the SLA performance
                section. Breach & at-risk cards are always shown.
              </p>

              <div className="sla-card-toggle-group">
                <h4>Performance</h4>
                <CardToggleRow
                  id="metRate"
                  title="Overall SLA met rate"
                  description="Shows the percentage of resolved tickets that met their resolution SLA in the selected window."
                />
                <CardToggleRow
                  id="frMet"
                  title="First response SLA met"
                  description="Number of tickets that met their first-response SLA in the selected window."
                />
                <CardToggleRow
                  id="resMet"
                  title="Resolution SLA met"
                  description="Number of tickets that met their resolution SLA in the selected window."
                />
                <CardToggleRow
                  id="paused"
                  title="Paused tickets"
                  description="Total number of open tickets currently in a paused SLA state."
                />
              </div>

              <div className="sla-card-toggle-group">
                <h4>Volume & speed</h4>
                <CardToggleRow
                  id="volumeTotal"
                  title="All tickets (all-time)"
                  description="Shows total tickets across your help desk and how many are currently open or resolved."
                />
                <CardToggleRow
                  id="volumeWindow"
                  title="Tickets submitted in window"
                  description="How many tickets were created in the currently selected time window."
                />
                <CardToggleRow
                  id="avgFr"
                  title="Average first response time"
                  description="Average time it takes agents to send the first response in the selected window."
                />
                <CardToggleRow
                  id="avgRes"
                  title="Average resolution time"
                  description="Average time to fully resolve tickets in the selected window."
                />
              </div>

              <div className="sla-card-toggle-footer">
                <button
                  type="button"
                  className="sla-card-btn-cancel"
                  onClick={() => setCardModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="sla-card-btn-save"
                  onClick={handleSaveCardPrefs}
                >
                  Save changes
                </button>
              </div>
            </div>
          </Slamodal>

          {/* New ticket modal */}
          {canCreateTicket && (
            <Slamodal
              isOpen={showNewTicket}
              onClose={() => setShowNewTicket(false)}
              title="Create Ticket"
            >
              <NewTicketForm
                companyId={companyId}
                submitterId={user?.uid}
                submitterName={employeeName || currentUserDisplayName}
                onClose={() => setShowNewTicket(false)}
                onCreated={() => refreshTickets(true)}
              />
            </Slamodal>
          )}
        </div>
      </div>
      <HelpdeskWelcomeTour
        open={showHelpdeskTour}
        isEmployer={isEmployer}
        isHelpdeskUser={myGroup === "IT"}
        onOpenSidebar={() => setSidebarOpen(true)}
        onClose={async () => {
          setShowHelpdeskTour(false);
          try {
            await setHelpdeskTourSeen(db, companyId, {
              seenBy: user?.uid,
              role: isEmployer ? "employer" : "employee",
            });
          } catch {}
        }}
        onDontShowAgain={(dontShow) => {
          // Optional: if you want "dont show again" to be user-specific,
          // you can store it in localStorage keyed by userId+companyId.
          if (dontShow) {
            localStorage.setItem(
              `oiko_helpdesk_tour_skip_${companyId}_${user?.uid}`,
              "1",
            );
          }
        }}
      />
    </div>
  );
}
